import React from 'react';
import { Page, View } from '@react-pdf/renderer';
import { PrintDataFields, getPrintHeaderVariant } from '@repo/print-pdf/arbre/print';
import {
  pdfStyles,
  PDFHeader,
  PDFInfoField,
  PDFSectionHeader,
  PdfEssenceIllustrationPage,
} from '@repo/print-pdf/shared';
import { imageService } from '@repo/ui/lib/services';
import { CommentairesBlock, DiagnosticSanitaireBlock, DiagnosticVisuelBlock } from '../blocks';

interface PDFDocumentProps {
  data: PrintDataFields;
  inventaireId?: number;
}

export const PDFDocument: React.FC<PDFDocumentProps> = ({
  data,
  inventaireId,
}) => {
  const headerVariant = getPrintHeaderVariant(data.inventaireType);

  return (

    <>
      {/* Page 1 */}
      <Page size="A4" style={pdfStyles.page}>
        {/* Header Section */}
        <PDFHeader
          organizationName={data.organizationName}
          organizationAddress={data.organizationAddress}
          organizationCity={data.organizationCity}
          organizationZipCode={data.organizationZipCode}
          organizationSiret={data.organizationSiret}
          organizationLogo={data.organizationLogo}
          user={data.user}
          logoSize="default"
        />

        {/* Main Title */}
        <PDFSectionHeader
          title={`FICHE DE SYNTHESE ARBRE - ${inventaireId}`}
          variant={headerVariant}
        />

        {/* Basic Information */}
        <View style={pdfStyles.grid3}>
          <View style={pdfStyles.columnNoLeft}>
            <PDFInfoField label="Date" value={data.formattedDate} />
            <PDFInfoField label="Genre (latin)" value={data.genreLatin} />
            <PDFInfoField label="Nom (FR)" value={data.especeLabel} />
            <PDFInfoField label="Adresse" value={data.addressLine} />
          </View>
          <View style={pdfStyles.columnNoLeft}>
            <PDFInfoField label="Code site" value={data.code_site} />
            <PDFInfoField label="Espèce" value={data.especeLatin} />
            <PDFInfoField label="Cultivar" value={data.cultivar} />
          </View>
          <View style={pdfStyles.columnNoLeft}>
            <PDFInfoField label="Identification" value={data.num_sujet} />
            <PDFInfoField label="Coordonnées GPS" value={data.gps} />
          </View>
        </View>

        {/* Caractéristiques */}
        <PDFSectionHeader title="CARACTÉRISTIQUES" variant="secondary" />
        <View style={pdfStyles.grid3}>
          <View style={pdfStyles.column}>
            <PDFInfoField label="Hauteur en m" value={data.hauteur} />
            <PDFInfoField label="Diamètre en cm" value={data.diametre} />
            <PDFInfoField label="Âge estimé" value={data.age_estime} />
            <PDFInfoField label="Stade de dév." value={data.caracteristiques.stade_dev} />
            <PDFInfoField label="Remarquable" value={data.caracteristiques.critere} />
          </View>
          <View style={pdfStyles.column}>
            <PDFInfoField label="Port" value={data.caracteristiques.port_arbre} />
            <PDFInfoField label="Tronc" value={data.caracteristiques.caract_tronc} />
            <PDFInfoField label="Pied de l'arbre" value={data.caracteristiques.caract_pied} />
          </View>
          <View style={pdfStyles.column}>
            <PDFInfoField label="Implantation" value={data.caracteristiques.implantation} />
            <PDFInfoField label="Domaine" value={data.caracteristiques.domaine} />
            <PDFInfoField label="Situation" value={data.caracteristiques.situation} />
            <PDFInfoField label="Accès" value={data.caracteristiques.accessibilite} />
          </View>
        </View>

        {/* Environnement */}
        <PDFSectionHeader title="ENVIRONNEMENT" variant="secondary" />
        <View style={pdfStyles.grid3}>
          <View style={pdfStyles.column}>
            <PDFInfoField label="Cohabitation" value={data.environnement.proximite} />
            <PDFInfoField label="Demande de DICT" value={data.environnement.dictLabel} />
          </View>
          <View style={pdfStyles.column}>
            <PDFInfoField label="Nuisance" value={data.environnement.nuisance} />
          </View>
          <View style={pdfStyles.column}>
            <PDFInfoField label="Fréquentation" value={data.environnement.taux_freq} />
            <PDFInfoField label="Type de Passage" value={data.environnement.type_passage} />
          </View>
        </View>

        {/* Diagnostic Visuel */}
        <PDFSectionHeader title="DIAGNOSTIC VISUEL" variant="secondary" />
        <DiagnosticVisuelBlock data={data} />
      </Page>

      {/* Page 2 - Diagnostic Sanitaire */}
      <Page size="A4" style={pdfStyles.page}>
        {/* Diagnostic sanitaire */}
        <PDFSectionHeader title="Diagnostic sanitaire" variant="secondary" />

        <DiagnosticSanitaireBlock data={data} />


        {/* Commentaires */}
        <CommentairesBlock data={data} />
      </Page>

      <PdfEssenceIllustrationPage
        latinName={data.especeLatin?.trim() || data.espece?.name || 'Essence'}
        frenchName={data.especeLabel?.trim() || data.espece?.nomFr || data.espece?.nom_fr || null}
        imageUrl={
          data.espece?.img_url
            ? imageService.getOptimizedImageUrl(data.espece.img_url, 'espece', 800, 600)
            : null
        }
      />
    </>
  );
};
